import { Button, Spin, Flex } from "antd";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { ImageUploader } from "./components";
import classNames from "~utils/classNames";
import { useUploadImage, useDeleteImage } from "./hooks";
import type { Image } from "~types/image";
import type { ImageBaseTypes } from "~enums/imageBaseTypes";
import type { ImageTypes } from "~enums/imageTypes";

import "./UploadImageForm.scss";

type Props = {
  baseType: ImageBaseTypes;
  baseId: string;
  imageType: ImageTypes;
  initialImage?: Image;
  permissionKey: PermissionNames;
};

const PlaceholderImage = () => (
  <svg>
    <line x1="0" y1="100%" x2="100%" y2="0"/>
    <line x1="0" y1="0" x2="100%" y2="100%"/>
  </svg>
);

const UploadImageForm = ({ baseType, baseId, imageType, initialImage, permissionKey }: Props) => {
  const { hasPermission } = useUserPermissions();
  const [currentImage, setCurrentImage] = useState<Image | undefined>(initialImage);
  const [preview, setPreview] = useState<string>(initialImage ? `${initialImage.url}?size=RAW` : "");
  const [imageToUpload, setImageToUpload] = useState<File>();
  const { mutateAsync: uploadImage, isPending: isImageUploading } = useUploadImage();
  const { mutateAsync: deleteImage, isPending: isImageClearing } = useDeleteImage();
  const hasPermissionToEditImage = hasPermission(permissionKey, PermissionOperations.UPDATE);
  const isLoading = isImageUploading || isImageClearing;

  const handleClear = async () => {
    if (imageToUpload) {
      setImageToUpload(undefined);
      setPreview(currentImage ? `${currentImage.url}?size=RAW` : "");
    } else if (currentImage) {
      await deleteImage(currentImage.imageId);
      setPreview("");
    }
  };

  const handleDrop = (file) => {
    setImageToUpload(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = () => uploadImage({ baseId, baseType, file: imageToUpload!, imageType }).then((response) => {
    setImageToUpload(undefined);
    setCurrentImage(response?.data?.[0]);
  });

  return (
    <div className="upload-form-wrapper">
      <div className={classNames("image-wrapper", isLoading && "is-loading")}>
        {isLoading && <Spin size="large" className="spinner" />}
        {preview ? <img alt={imageType} src={preview} /> : <PlaceholderImage />}
      </div>
      {hasPermissionToEditImage && (
        <>
          <ImageUploader handleDrop={handleDrop} disabled={isLoading} />
          <Flex gap={8} justify="flex-end" align="center">
            <Button onClick={handleClear} disabled={!imageToUpload && !preview}>
              <FormattedMessage id="image.clear" />
            </Button>
            <Button type="primary" onClick={handleUpload} disabled={!imageToUpload}>
              <FormattedMessage id="image.upload" />
            </Button>
          </Flex>
        </>
      )}
    </div>
  );
};

export default UploadImageForm;
